import type RiskEntityReferentialRepository from "@/core/administration/repositories/RiskEntityReferentialRepository";
import {
  EntityType,
  type RiskEntityCode,
  type RiskEntityCodeResponse,
  type RiskEntityFilters,
  type RiskEntityTypeStats,
  type RiskLevel,
} from "@/core/administration/entities/RiskEntityCode";
import type { RiskEntityHistoryEvent } from "@/core/administration/entities/RiskEntityHistoryEvent";
import type {
  ImportResult,
  PreviewImportResult,
} from "@/core/administration/entities/RiskEntityImport";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import { riskEntityReferentialEndpoints } from "../../api/riskEntityReferentialEndpoints/riskEntityReferentialEndpoints";
import {
  type EntityReferentialDto,
  type ScoringEntityAuditDto,
  type CsvImportPreviewDto,
  type CsvImportSummaryDto,
  mapToRiskEntityCode,
  mapToHistoryEvent,
  mapToPreviewResult,
  mapToImportResult,
  mapToTypeStats,
} from "./mappers";

export default class RiskEntityReferentialRepositoryBackend implements RiskEntityReferentialRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getTypeStats(): Promise<RepositoryResult<RiskEntityTypeStats[]>> {
    try {
      const types = Object.values(EntityType);
      const stats: RiskEntityTypeStats[] = await Promise.all(
        types.map(async (type) => {
          const resp = await this.httpClient.get<ApiResponse<EntityReferentialDto[]>>({
            url: riskEntityReferentialEndpoints.codes(type),
            withAuth: true,
          });
          const result = ApiResponseHandler.handleResponse<EntityReferentialDto[]>(resp);
          return mapToTypeStats(type, result.data ?? []);
        }),
      );
      return RepositoryResultFactory.success(stats);
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async getCodes(
    type: EntityType,
    _filters: RiskEntityFilters,
  ): Promise<RepositoryResult<RiskEntityCodeResponse>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<EntityReferentialDto[]>>({
          url: riskEntityReferentialEndpoints.codes(type),
          withAuth: true,
        }),
      (dtos) => {
        const items = dtos.map(mapToRiskEntityCode);
        return { items, totalElements: items.length };
      },
    );
  }

  async createCode(
    type: EntityType,
    data: Pick<RiskEntityCode, "code" | "libelle" | "level">,
  ): Promise<RepositoryResult<RiskEntityCode>> {
    return executeRequest(
      () =>
        this.httpClient.post<
          ApiResponse<EntityReferentialDto>,
          { code: string; label: string; riskLevel: string }
        >({
          url: riskEntityReferentialEndpoints.codes(type),
          body: { code: data.code, label: data.libelle, riskLevel: data.level },
          withAuth: true,
        }),
      mapToRiskEntityCode,
    );
  }

  async updateCode(
    type: EntityType,
    code: string,
    newLevel: RiskLevel,
    reason: string,
    label: string,
  ): Promise<RepositoryResult<RiskEntityCode>> {
    const body = { riskLevel: newLevel, reason, label };

    return executeRequest(
      () =>
        this.httpClient.put<ApiResponse<EntityReferentialDto>, typeof body>({
          url: riskEntityReferentialEndpoints.updateCode(type, code),
          body,
          withAuth: true,
        }),
      mapToRiskEntityCode,
    );
  }

  async deactivateCode(
    type: EntityType,
    code: string,
    reason?: string,
  ): Promise<RepositoryResult<RiskEntityCode>> {
    const body = reason ? { reason } : {};
    return executeRequest(
      () =>
        this.httpClient.post<ApiResponse<EntityReferentialDto>, typeof body>({
          url: riskEntityReferentialEndpoints.deactivate(type, code),
          body,
          withAuth: true,
        }),
      mapToRiskEntityCode,
    );
  }

  async getHistory(
    type: EntityType,
  ): Promise<RepositoryResult<RiskEntityHistoryEvent[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<ScoringEntityAuditDto[]>>({
          url: riskEntityReferentialEndpoints.history(type),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapToHistoryEvent),
    );
  }

  async rollback(
    type: EntityType,
    version: number,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<unknown>, Record<string, never>>({
        url: riskEntityReferentialEndpoints.rollback(type, version),
        body: {},
        withAuth: true,
      }),
    );
  }

  async previewImport(
    type: EntityType,
    file: File,
  ): Promise<RepositoryResult<PreviewImportResult>> {
    const fd = new FormData();
    fd.append("file", file);

    return executeRequest(
      () =>
        this.httpClient.post<ApiResponse<CsvImportPreviewDto>, FormData>({
          url: riskEntityReferentialEndpoints.importPreview(type),
          body: fd,
          withAuth: true,
        }),
      mapToPreviewResult,
    );
  }

  async confirmImport(
    type: EntityType,
    importToken: string,
    confirmedBy: string,
  ): Promise<RepositoryResult<ImportResult>> {
    return executeRequest(
      () =>
        this.httpClient.post<
          ApiResponse<CsvImportSummaryDto>,
          { importToken: string; confirmedBy: string }
        >({
          url: riskEntityReferentialEndpoints.importConfirm(type),
          body: { importToken, confirmedBy },
          withAuth: true,
        }),
      mapToImportResult,
    );
  }
}
